import { Search, Award } from "lucide-react";
import { Button } from "@/components/ui/button";
import PacManLoader from "@/pages/worth/PacManLoader";

const usd = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
const VERDICT = {
  yes: "border-emerald-200 bg-emerald-50 text-emerald-900",
  maybe: "border-amber-200 bg-amber-50 text-amber-900",
  no: "border-slate-200 bg-slate-50 text-slate-700",
  graded: "border-blue-200 bg-blue-50 text-blue-900",
  unknown: "border-slate-200 bg-slate-50 text-slate-600",
};

function Col({ label, s, id, onPick }) {
  const body = (
    <>
      <p className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-mono text-base font-bold text-slate-900" data-testid={`card-price-${id}-median`}>{usd(s?.median)}</p>
      <p className="font-mono text-[10px] text-slate-400">{s?.count ? `${s.count} listed · ${usd(s.low)}–${usd(s.high)}` : "no listings"}</p>
    </>
  );
  if (!onPick || !s?.median) return <div className="min-w-0 rounded-md border border-slate-200 bg-white p-2" data-testid={`card-price-${id}`}>{body}</div>;
  return (
    <button type="button" onClick={() => onPick(s.median)} title="Use this as my price" data-testid={`card-price-${id}`}
      className="min-w-0 rounded-md border border-slate-200 bg-white p-2 text-left transition-colors hover:border-[#0053DA] hover:bg-blue-50/40">
      {body}
    </button>
  );
}

export default function CardPrices({ prices, busy, onCheck, onPick }) {
  const p = prices?.checked_at ? prices : null;
  const same = p?.same_grade;
  return (
    <div className="rounded-md border border-slate-200 bg-slate-100/60 p-2" data-testid="card-prices">
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 text-[11px] font-semibold text-slate-800">Raw vs graded prices <span className="font-normal text-slate-500">(live eBay asking prices)</span></p>
        {onCheck && (
          <Button size="sm" variant="outline" onClick={onCheck} disabled={busy} className="h-7 shrink-0 rounded-full px-2.5 text-[11px]" data-testid="card-prices-check-btn">
            {busy ? <span className="mr-1"><PacManLoader size={11} testid="card-prices-pacman" /></span> : <Search className="mr-1 h-3 w-3" />}{p ? "Re-check" : "Check prices"}
          </Button>
        )}
      </div>
      {!p && busy && <p className="mt-2 text-[11px] text-slate-500">Checking raw, PSA 9 and PSA 10 listings…</p>}
      {p && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-3 gap-1.5">
            <Col label="Raw" s={p.raw} id="raw" onPick={onPick} />
            <Col label="PSA 9" s={p.psa9} id="psa9" onPick={onPick} />
            <Col label="PSA 10" s={p.psa10} id="psa10" onPick={onPick} />
          </div>
          {same && <Col label="Same grade as yours" s={same} id="same" onPick={onPick} />}
          {p.verdict && (
            <p className={`flex gap-1.5 rounded-md border p-2 text-[11px] leading-relaxed ${VERDICT[p.verdict.level] || VERDICT.unknown}`} data-testid="card-grading-verdict">
              <Award className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span><b>Worth grading?</b> {p.verdict.text}</span>
            </p>
          )}
          <p className="text-[10px] text-slate-400">Search: “{p.query}”{onPick ? " · tap a price to use it" : ""}</p>
        </div>
      )}
    </div>
  );
}
